/**
 * Maybourne Product Gallery Enhancement
 * 
 * Handles product gallery grid layout enhancements:
 * - Makes the last image span 2 columns when it's alone in the bottom row (grid_2x_highlighted layout)
 */

export default class ProductGallery {
  constructor(container = document) {
    this.container = container
    this.galleries = []
    this.init()
  }

  init() {
    this.enhanceGalleryLayout()
    this.bindEvents()
  }

  enhanceGalleryLayout() {
    // Only apply on desktop (min-width: 1000px)
    if (window.innerWidth < 1000) return

    const productGalleries = this.container.querySelectorAll('.product-gallery')
    
    productGalleries.forEach(gallery => {
      this.processGallery(gallery)
    })
  }

  processGallery(gallery) {
    const mediaElements = gallery.querySelectorAll('.product-gallery__media:not([hidden])')
    
    if (mediaElements.length < 2) return // No need to process if less than 2 images

    const totalImages = mediaElements.length
    
    // Reset all elements first
    mediaElements.forEach(media => {
      media.style.gridColumn = ''
      media.classList.remove('maybourne-gallery-media--full-width')
    })

    // Check if we're in a grid_2x_highlighted layout by checking computed styles
    const firstMedia = mediaElements[0]
    const computedStyle = window.getComputedStyle(firstMedia)
    
    // If first image has higher grid column span, it's likely grid_2x_highlighted
    if (computedStyle.gridColumn.includes('span 2') || computedStyle.gridColumn.includes('1 / 3')) {
      // In grid_2x_highlighted layout:
      // - 1st image: spans 2 columns (full width)
      // - Remaining images: arranged in pairs (2 per row)
      // - If odd number of remaining images, last one is alone and should span 2 columns
      
      const remainingImages = totalImages - 1 // Subtract the first full-width image
      const isLastImageAlone = remainingImages % 2 === 1 // Odd number of remaining images
      
      if (isLastImageAlone) {
        const lastMedia = mediaElements[totalImages - 1]
        lastMedia.style.gridColumn = 'span 2'
        lastMedia.classList.add('maybourne-gallery-media--full-width')
      }
    }
  }

  bindEvents() {
    // Handle window resize
    let resizeTimeout
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        this.enhanceGalleryLayout()
      }, 250)
    })

    // Handle dynamic content changes
    const observer = new MutationObserver((mutations) => {
      let shouldReprocess = false
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          const target = mutation.target
          if (target.classList?.contains('product-gallery') || 
              target.closest('.product-gallery') ||
              target.classList?.contains('product-gallery__media')) {
            shouldReprocess = true
          }
        }
      })
      
      if (shouldReprocess) {
        setTimeout(() => this.enhanceGalleryLayout(), 100)
      }
    })

    // Observe changes in the container
    observer.observe(this.container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'style', 'class']
    })
  }

  destroy() {
    // Reset all modified elements
    const mediaElements = this.container.querySelectorAll('.product-gallery__media')
    mediaElements.forEach(media => {
      media.style.gridColumn = ''
      media.classList.remove('maybourne-gallery-media--full-width')
    })
  }
} 